import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash, Plus, X } from "@phosphor-icons/react";
import { Header, F, IconBtn, Modal } from "@/components/Clients";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

const emptyLine = { description: "", quantity: 1, rate: 0 };
const empty = {
  client_id: "",
  invoice_number: "",
  issue_date: new Date().toISOString().slice(0, 10),
  due_date: "",
  lines: [{ ...emptyLine }],
  tax_percent: 18.0,
  notes: "",
  status: "draft",
};

const Invoices = ({ readOnly = false }) => {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/invoices").then((r) => setRows(r.data.invoices));
  useEffect(() => {
    load();
    if (!readOnly) api.get("/clients").then((r) => setClients(r.data.clients));
  }, [readOnly]);

  const subtotal = form.lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.rate || 0), 0);
  const tax = (subtotal * Number(form.tax_percent || 0)) / 100;
  const total = subtotal + tax;

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setLine = (i, k, v) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], [k]: v };
    setForm({ ...form, lines });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        tax_percent: Number(form.tax_percent),
        lines: form.lines.map((l) => ({ ...l, quantity: Number(l.quantity), rate: Number(l.rate) })),
      };
      await api.post("/invoices", payload);
      toast.success("Invoice created");
      setShowForm(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const setStatus = async (inv, status) => {
    await api.patch(`/invoices/${inv.id}/status`, { status });
    load();
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete ${inv.invoice_number}?`)) return;
    await api.delete(`/invoices/${inv.id}`);
    load();
  };

  return (
    <div>
      <Header
        title="Invoices"
        subtitle="Retainer & advisory billing"
        onNew={readOnly ? undefined : () => { setForm(empty); setShowForm(true); }}
        newLabel="New Invoice"
        testId="invoice"
      />

      <div className="mt-6 border border-[#E8EAE8] bg-white">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#E8EAE8] text-[11px] uppercase tracking-widest text-[#5C6B64] font-semibold">
          <div className="col-span-2 font-mono-data">Number</div>
          <div className="col-span-3">Client</div>
          <div className="col-span-2 font-mono-data">Issued</div>
          <div className="col-span-2 font-mono-data">Due</div>
          <div className="col-span-1 text-right font-mono-data">Total</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {rows.length === 0 && <div className="p-6 text-sm text-[#5C6B64]">No invoices yet.</div>}
        {rows.map((inv) => (
          <div key={inv.id} data-testid={`inv-row-${inv.id}`} className="grid grid-cols-12 gap-4 px-5 py-4 border-b last:border-b-0 border-[#E8EAE8] items-center">
            <div className="col-span-2 font-mono-data text-[#0F2E22]">{inv.invoice_number}</div>
            <div className="col-span-3">{inv.client_name}</div>
            <div className="col-span-2 font-mono-data text-xs">{inv.issue_date}</div>
            <div className="col-span-2 font-mono-data text-xs">{inv.due_date}</div>
            <div className="col-span-1 text-right font-mono-data text-[#0F2E22]">{fmt(inv.total)}</div>
            <div className="col-span-1">
              {readOnly ? (
                <span className={`text-[10px] uppercase tracking-widest ${statusColor(inv.status)}`}>{inv.status}</span>
              ) : (
                <select
                  data-testid={`status-select-${inv.id}`}
                  value={inv.status}
                  onChange={(e) => setStatus(inv, e.target.value)}
                  className={`text-[10px] uppercase tracking-widest bg-transparent border-none outline-none ${statusColor(inv.status)}`}
                >
                  {["draft", "sent", "paid", "overdue"].map((s) => <option key={s}>{s}</option>)}
                </select>
              )}
            </div>
            <div className="col-span-1 flex justify-end gap-2">
              {!readOnly && <IconBtn onClick={() => remove(inv)} testId={`del-inv-${inv.id}`}><Trash size={14} /></IconBtn>}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <Modal title="New Invoice" onClose={() => setShowForm(false)}>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-[#5C6B64]">Client</span>
                <select data-testid="form-client" required value={form.client_id} onChange={setF("client_id")} className="mt-1 w-full border border-[#E8EAE8] px-3 py-2 text-sm bg-white">
                  <option value="">— Select —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <F label="Invoice number (optional)" value={form.invoice_number} onChange={setF("invoice_number")} mono testId="form-num" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <F label="Issue date" type="date" value={form.issue_date} onChange={setF("issue_date")} required testId="form-issue" />
              <F label="Due date" type="date" value={form.due_date} onChange={setF("due_date")} required testId="form-due" />
              <F label="GST %" type="number" step="0.01" value={form.tax_percent} onChange={setF("tax_percent")} mono testId="form-tax" />
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-[#5C6B64] mb-2">Line items</div>
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center mb-2">
                  <input placeholder="Description" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} className="col-span-6 border border-[#E8EAE8] px-2 py-1.5 text-sm" data-testid={`line-desc-${i}`} required />
                  <input placeholder="Qty" type="number" step="0.01" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="col-span-2 border border-[#E8EAE8] px-2 py-1.5 text-sm font-mono-data" data-testid={`line-qty-${i}`} />
                  <input placeholder="Rate" type="number" step="0.01" value={l.rate} onChange={(e) => setLine(i, "rate", e.target.value)} className="col-span-3 border border-[#E8EAE8] px-2 py-1.5 text-sm font-mono-data" data-testid={`line-rate-${i}`} />
                  <button type="button" onClick={() => setForm({ ...form, lines: form.lines.filter((_, x) => x !== i) })} className="col-span-1 text-[#A31A1A] justify-self-end" data-testid={`rm-line-${i}`}><X size={14} /></button>
                </div>
              ))}
              <button type="button" onClick={() => setForm({ ...form, lines: [...form.lines, { ...emptyLine }] })} className="mt-1 text-xs flex items-center gap-1 text-[#0F2E22] hover:underline" data-testid="add-line-btn">
                <Plus size={12} /> Add line
              </button>
            </div>

            <div className="border-t border-[#E8EAE8] pt-3 font-mono-data text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#5C6B64]">Subtotal</span><span>{fmt(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#5C6B64]">GST ({form.tax_percent}%)</span><span>{fmt(tax)}</span></div>
              <div className="flex justify-between text-lg text-[#0F2E22]"><span>Total</span><span data-testid="inv-total">{fmt(total)}</span></div>
            </div>

            <F label="Notes" value={form.notes} onChange={setF("notes")} testarea testId="form-notes" />

            <button type="submit" data-testid="save-inv-btn" className="w-full py-3 bg-[#0F2E22] text-white text-sm tracking-widest uppercase hover:opacity-90">Save</button>
          </form>
        </Modal>
      )}
    </div>
  );
};

const statusColor = (s) => {
  switch (s) {
    case "paid": return "text-[#2D7A4D]";
    case "overdue": return "text-[#A31A1A]";
    case "sent": return "text-[#1A44A8]";
    default: return "text-[#5C6B64]";
  }
};

export default Invoices;
